import { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Switch,
  FormControlLabel,
  useMediaQuery,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { Send, Mail, Lock, Phone, AccountCircle } from "@mui/icons-material";
import { db, auth } from "./firebase"; // Import Firebase
import { doc, setDoc } from "firebase/firestore"; // Firestore functions
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth"; // Firebase Auth functions

const ChatOnboarding = () => {
  const [step, setStep] = useState(0);
  const [role, setRole] = useState(null);
  const [language, setLanguage] = useState("English"); // Default language
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    grade: "",
    subjects: "",
    location: "",
    qualifications: "",
    gradePreferences: "",
    additionalDetails: "",
    otp: "",
    countryCode: "+91", // Default country code for India
  });
  const [chatHistory, setChatHistory] = useState([]); // To store chat history (questions and answers)
  const [confirmationResult, setConfirmationResult] = useState(null); // To store the OTP confirmation result
  const [theme, setTheme] = useState("dark"); // Theme state: 'light' or 'dark'

  // Check screen size for responsiveness
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:960px)");

  // Toggle theme between light and dark
  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  // Validates email format
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Validate phone number (digits only)
  const validatePhoneNumber = (phone) => {
    return /^\d+$/.test(phone);
  };

  // Language-specific messages
  const messages = {
    English: [
      {
        text: "In which language would you like to continue?",
        options: ["English", "Hindi"],
      },
      {
        text: "Are you registering as a student or teacher?",
        options: ["Student", "Teacher"],
      },
      role === "Student"
        ? { text: "What grade are you in?", input: "grade" }
        : null,
      role === "Student"
        ? { text: "What subjects are you interested in?", input: "subjects" }
        : null,
      role === "Student"
        ? { text: "Where are you located?", input: "location" }
        : null,
      role === "Teacher"
        ? {
            text: "What are your qualifications?",
            input: "qualifications",
          }
        : null,
      role === "Teacher"
        ? {
            text: "Which subjects do you wish to teach?",
            input: "subjects",
          }
        : null,
      role === "Teacher"
        ? {
            text: "What are your grade preferences?",
            input: "gradePreferences",
          }
        : null,
      role === "Teacher"
        ? {
            text: "Please provide Aadhar card Number for verification:",
            input: "number",
          }
        : null,
      { text: "Please enter your full name:", input: "name" },
      { text: "Enter your email:", input: "email", icon: <Mail /> },
      role === "Student"
        ? {
            text: "Create a password:",
            input: "password",
            icon: <Lock />,
            type: "password",
          }
        : null,
      { text: "Enter your phone number:", input: "phone", icon: <Phone /> },
      {
        text: "Verifying details... Please enter OTP sent to your phone.",
        input: "otp",
      },
      {
        text: "Thank you for providing your details. Registration complete!",
      },
    ],
    Hindi: [
      {
        text: "आप किस भाषा में आगे बढ़ना चाहेंगे?",
        options: ["अंग्रेज़ी", "हिंदी"],
      },
      {
        text: "क्या आप एक छात्र या शिक्षक के रूप में पंजीकरण कर रहे हैं?",
        options: ["छात्र", "शिक्षक"],
      },
      role === "Student"
        ? { text: "आप किस कक्षा में हैं?", input: "grade" }
        : null,
      role === "Student"
        ? { text: "आपकी रुचि के विषय क्या हैं?", input: "subjects" }
        : null,
      role === "Student"
        ? { text: "आप कहाँ स्थित हैं?", input: "location" }
        : null,
      role === "Teacher"
        ? {
            text: "आपकी योग्यताएं क्या हैं?",
            input: "qualifications",
          }
        : null,
      role === "Teacher"
        ? {
            text: "आप कौन से विषय पढ़ाना चाहते हैं?",
            input: "subjects",
          }
        : null,
      role === "Teacher"
        ? {
            text: "आपकी कक्षा वरीयताएं क्या हैं?",
            input: "gradePreferences",
          }
        : null,
      role === "Teacher"
        ? {
            text: "कृपया सत्यापन के लिए आधार कार्ड नंबर प्रदान करें:",
            input: "number",
          }
        : null,
      { text: "कृपया अपना पूरा नाम दर्ज करें:", input: "name" },
      { text: "अपना ईमेल दर्ज करें:", input: "email", icon: <Mail /> },
      role === "Student"
        ? {
            text: "एक पासवर्ड बनाएं:",
            input: "password",
            icon: <Lock />,
            type: "password",
          }
        : null,
      { text: "अपना फोन नंबर दर्ज करें:", input: "phone", icon: <Phone /> },
      {
        text: "विवरण सत्यापित किया जा रहा है... कृपया अपने फोन पर भेजे गए OTP को दर्ज करें।",
        input: "otp",
      },
      {
        text: "आपके विवरण प्रदान करने के लिए धन्यवाद। पंजीकरण पूरा हो गया है!",
      },
    ],
  };

  // Filter out null values to avoid indexing issues
  const filteredMessages = messages[language].filter((msg) => msg !== null);

  // Save user data to Firestore
  const saveUserData = async (data) => {
    try {
      // Generate a unique ID for the user (since there's no authenticated user)
      const userId = `user_${Date.now()}`; // Example: "user_1234567890"

      // Reference to the user's document
      const userRef = doc(db, "users", userId);

      // Save the data to Firestore
      console.log("Saving data to Firestore:", data);
      await setDoc(userRef, data);
      console.log("User data saved to Firestore");
    } catch (error) {
      console.error("Error saving user data:", error);
    }
  };

  const handleSelect = (option) => {
    if (step === 0) {
      // Language selection step
      setLanguage(option); // Directly set the language based on the selected option
      setStep((prev) => prev + 1);
    } else if (step === 1) {
      // Role selection step
      // Update role based on the selected option (works for both English and Hindi)
      if (option === "Student" || option === "छात्र") {
        setRole("Student");
      } else if (option === "Teacher" || option === "शिक्षक") {
        setRole("Teacher");
      }
      setStep((prev) => prev + 1);
    }

    // Add the question and user's response to chat history
    const newChatHistory = [
      ...chatHistory,
      {
        question: filteredMessages[step].text, // Current question
        answer: option, // User's response
      },
    ];
    setChatHistory(newChatHistory);

    // Save the role and chat history to Firestore
    saveUserData({ role: option, chatHistory: newChatHistory });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryCodeChange = (e) => {
    setUserData((prev) => ({ ...prev, countryCode: e.target.value }));
  };

  // Send OTP to the user's phone number
  const sendOTP = async (phoneNumber) => {
    try {
      if (phoneNumber.length !== 10) {
        alert("Please enter a valid 10-digit phone number.");
        return;
      }

      const appVerifier = window.recaptchaVerifier;
      if (!appVerifier) {
        console.error("reCAPTCHA verifier not initialized.");
        return;
      }

      // Combine the selected country code with the phone number
      const formattedPhoneNumber = `${userData.countryCode}${phoneNumber}`;

      console.log("Formatted Phone Number:", formattedPhoneNumber);

      const result = await signInWithPhoneNumber(
        auth,
        formattedPhoneNumber,
        appVerifier
      );
      setConfirmationResult(result); // Store the confirmation result
      console.log("OTP sent successfully");

      // Increment step to show OTP input box
      setStep((prev) => prev + 1);
    } catch (error) {
      console.error("Error sending OTP:", error);
      alert(`Failed to send OTP: ${error.message}`);
    }
  };

  // Initialize reCAPTCHA verifier
  useEffect(() => {
    if (step === 10 && auth) {
      try {
        console.log("Initializing reCAPTCHA...");

        // Ensure the container exists in the DOM
        const recaptchaContainer = document.getElementById(
          "recaptcha-container"
        );
        if (!recaptchaContainer) {
          console.error("reCAPTCHA container not found in the DOM.");
          return;
        }

        // Initialize reCAPTCHA verifier
        window.recaptchaVerifier = new RecaptchaVerifier(
          "recaptcha-container",
          {
            size: "invisible", // Use invisible reCAPTCHA
            callback: () => {
              console.log("reCAPTCHA solved");
            },
            "expired-callback": () => {
              console.log("reCAPTCHA expired");
            },
          },
          auth
        );

        console.log("reCAPTCHA initialized successfully");
      } catch (error) {
        console.error("Error initializing reCAPTCHA:", error);
      }
    }
  }, [step, auth]);

  const handleNext = async () => {
    console.log("Next button clicked"); // Log to confirm the function is called

    // Check if the input field is empty
    if (
      filteredMessages[step].input &&
      !userData[filteredMessages[step].input]
    ) {
      alert("Field can't be empty");
      return;
    }

    // Validate email format
    if (
      filteredMessages[step].input === "email" &&
      !validateEmail(userData.email)
    ) {
      alert("Please enter a valid email address.");
      return;
    }

    // Validate phone number format
    if (
      filteredMessages[step].input === "phone" &&
      (!validatePhoneNumber(userData.phone) || userData.phone.length !== 10)
    ) {
      alert("Please enter a valid 10-digit phone number.");
      return;
    }

    if (step < filteredMessages.length - 1) {
      // If the current step is for OTP, verify the OTP
      if (filteredMessages[step].input === "otp") {
        try {
          const otp = userData.otp;
          await confirmationResult.confirm(otp); // Confirm the OTP
          setStep((prev) => prev + 1); // Move to the next step
        } catch (error) {
          console.error("Error verifying OTP:", error);
          alert("Invalid OTP. Please try again.");
        }
      } else {
        // If the current step is for password, add a custom message to chat history
        if (filteredMessages[step].input === "password") {
          const newChatHistory = [
            ...chatHistory,
            {
              question: filteredMessages[step].text, // Current question
              answer: "Password has been set", // Custom message
            },
          ];
          setChatHistory(newChatHistory);
          await saveUserData({ ...userData, chatHistory: newChatHistory });
        } else {
          // For other inputs, add the user's input to chat history
          const newChatHistory = [
            ...chatHistory,
            {
              question: filteredMessages[step].text, // Current question
              answer: userData[filteredMessages[step].input], // User's input
            },
          ];
          setChatHistory(newChatHistory);
          await saveUserData({ ...userData, chatHistory: newChatHistory });
        }

        setStep((prev) => prev + 1);

        // If it's the last step, save user data to Firestore
        if (step === filteredMessages.length - 2) {
          await saveUserData(userData);
        }
      }
    }
  };

  // Theme styles
  const themeStyles = {
    light: {
      background: "linear-gradient(135deg, #f5f5f5, #e0e0e0)",
      cardBackground: "rgba(255, 255, 255, 0.9)",
      textColor: "#000000",
      headerBg: "rgba(255, 255, 255, 0.9)",
      headerText: "#1976d2",
      inputBg: "rgba(255, 255, 255, 0.9)",
      inputText: "#000000",
    },
    dark: {
      background: "linear-gradient(135deg, #1a1a1a, #0d0d0d)",
      cardBackground: "rgba(30, 30, 30, 0.8)",
      textColor: "#ffffff",
      headerBg: "rgba(0, 0, 0, 0.5)",
      headerText: "#1976d2",
      inputBg: "rgba(255, 255, 255, 0.1)",
      inputText: "#ffffff",
    },
  };

  const currentTheme = themeStyles[theme];

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{
        background: currentTheme.background,
        color: currentTheme.textColor,
        padding: isMobile ? "16px" : "24px", // Responsive padding
      }}
    >
      <Card
        sx={{
          width: isMobile ? "100%" : isTablet ? "80%" : "400px", // Responsive width
          borderRadius: 3,
          boxShadow: 5,
          background: currentTheme.cardBackground,
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        {/* Chat Header */}
        <Box
          sx={{
            bgcolor: currentTheme.headerBg,
            color: currentTheme.headerText,
            p: 2,
            borderTopLeftRadius: 3,
            borderTopRightRadius: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <AccountCircle fontSize="large" sx={{ color: "#1976d2" }} />
            <Typography variant={isMobile ? "h6" : "h5"}>
              KAA BHARAT Registration
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={theme === "dark"}
                onChange={toggleTheme}
                color="primary"
              />
            }
            label={theme === "dark" ? "Dark Theme" : "Light Theme"}
            sx={{ display: isMobile ? "none" : "flex" }} // Hide on mobile
          />
        </Box>

        <CardContent
          sx={{
            height: isMobile ? "400px" : "500px", // Responsive height
            overflowY: "auto",
            p: 2,
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#1976d2",
              borderRadius: "3px",
            },
          }}
        >
          {/* Chat History */}
          {filteredMessages.slice(0, step + 1).map((msg, index) => (
            <Box key={index}>
              {/* System Message */}
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                mb={2}
              >
                <Box
                  sx={{
                    bgcolor: currentTheme.inputBg,
                    p: 2,
                    borderRadius: 2,
                    maxWidth: "70%",
                    boxShadow: 1,
                    color: currentTheme.textColor,
                  }}
                >
                  <Typography variant="body1">{msg.text}</Typography>
                </Box>
              </Box>

              {/* User Response */}
              {chatHistory[index] && (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-end"
                  mb={2}
                >
                  <Box
                    sx={{
                      bgcolor: "#1976d2",
                      p: 2,
                      borderRadius: 2,
                      maxWidth: "70%",
                      color: "white",
                      boxShadow: 1,
                    }}
                  >
                    <Typography variant="body1">
                      {chatHistory[index].answer}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          ))}

          {/* Options */}
          {filteredMessages[step]?.options && (
            <Box display="flex" gap={2} mt={2}>
              {filteredMessages[step].options.map((option) => (
                <Button
                  key={option}
                  variant="contained"
                  sx={{
                    bgcolor: "#1976d2",
                    color: "white",
                    borderRadius: 2,
                    "&:hover": { bgcolor: "#f44336" },
                    boxShadow: 1,
                  }}
                  onClick={() => handleSelect(option)}
                >
                  {option}
                </Button>
              ))}
            </Box>
          )}

          {/* Input Field */}
          {filteredMessages[step]?.input === "otp" ? (
            <Box display="flex" alignItems="center" gap={1} mt={2}>
              <TextField
                type="text"
                name="otp"
                value={userData.otp || ""}
                onChange={handleInputChange}
                label="Enter OTP"
                fullWidth
                sx={{
                  bgcolor: currentTheme.inputBg,
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "#1976d2",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: currentTheme.textColor,
                  },
                  "& .MuiInputBase-input": {
                    color: currentTheme.textColor,
                  },
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                sx={{
                  bgcolor: "#1976d2",
                  color: "white",
                  "&:hover": { bgcolor: "#f44336" },
                }}
              >
                Verify OTP
              </Button>
            </Box>
          ) : filteredMessages[step]?.input === "phone" ? (
            <Box display="flex" alignItems="center" gap={1} mt={2}>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel id="country-code-label">Code</InputLabel>
                <Select
                  labelId="country-code-label"
                  value={userData.countryCode}
                  onChange={handleCountryCodeChange}
                  label="Code"
                  sx={{
                    bgcolor: currentTheme.inputBg,
                    color: currentTheme.textColor,
                  }}
                >
                  <MenuItem value="+91">+91 (India)</MenuItem>
                  <MenuItem value="+1">+1 (USA)</MenuItem>
                  <MenuItem value="+44">+44 (UK)</MenuItem>
                  {/* Add more country codes as needed */}
                </Select>
              </FormControl>
              <TextField
                type="text"
                name="phone"
                value={userData.phone || ""}
                onChange={handleInputChange}
                label="Phone Number"
                fullWidth
                sx={{
                  bgcolor: currentTheme.inputBg,
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "#1976d2",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: currentTheme.textColor,
                  },
                  "& .MuiInputBase-input": {
                    color: currentTheme.textColor,
                  },
                }}
              />
              <IconButton
                color="primary"
                onClick={() => sendOTP(userData.phone)}
                sx={{
                  bgcolor: "#1976d2",
                  color: "white",
                  "&:hover": { bgcolor: "#f44336" },
                }}
              >
                <Send />
              </IconButton>
            </Box>
          ) : filteredMessages[step]?.input ? (
            <Box display="flex" alignItems="center" gap={1} mt={2}>
              {filteredMessages[step].icon}
              <TextField
                type={filteredMessages[step].type || "text"}
                name={filteredMessages[step].input}
                value={userData[filteredMessages[step].input] || ""}
                onChange={handleInputChange}
                label="Type here..."
                fullWidth
                sx={{
                  bgcolor: currentTheme.inputBg,
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "#1976d2",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: currentTheme.textColor,
                  },
                  "& .MuiInputBase-input": {
                    color: currentTheme.textColor,
                  },
                }}
              />
              <IconButton
                color="primary"
                onClick={handleNext}
                sx={{
                  bgcolor: "#1976d2",
                  color: "white",
                  "&:hover": { bgcolor: "#f44336" },
                }}
              >
                <Send />
              </IconButton>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      {/* reCAPTCHA Container (Invisible) */}
      <div id="recaptcha-container"></div>
    </Box>
  );
};

export default ChatOnboarding;
